import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowDown, Check, ArrowUpRight, Award, Globe, Users, Sparkles } from 'lucide-react';
import TechnologyCard from '../components/TechnologyCard';
import ContactForm from '../components/ContactForm';

const HomePage = () => {
  const technologies = [
    {
      name: 'EndoliftX',
      tagline: 'Non-Surgical Laser Lifting',
      description: 'Revolutionary laser face lifting available in NZ. Immediate skin tightening results with zero downtime — the gold standard in non-surgical lifting.',
      image: '/endoliftx-hero.jpg',
      path: '/technology/endoliftx',
      variant: 'wide',
    },
    {
      name: 'Jalupro',
      tagline: 'Bio-Revitalization',
      description: 'Collagen-stimulating injectable treatment for skin rejuvenation. Trusted by dermatologists worldwide for 20+ years.',
      image: '/jalupro-hero.jpg',
      path: '/technology/jalupro',
      variant: 'default',
    },
    {
      name: 'Aqualyx',
      tagline: 'The World\'s Leading Fat Dissolving Solution',
      description: 'Non-surgical fat reduction treatment for double chin and stubborn fat areas. Over 3 million treatments performed globally.',
      image: '/aqualyx-hero.png',
      path: '/technology/aqualyx',
      variant: 'default',
    },
    {
      name: 'Alidya',
      tagline: 'Cellulite Treatment',
      description: 'The world\'s first injectable cellulite treatment. Clinically proven to reduce cellulite appearance and improve skin texture.',
      image: '/alidya-hero.png',
      path: '/technology/alidya',
      variant: 'default',
    },
    {
      name: 'MCT',
      tagline: 'Regenerative Medicine',
      description: 'Advanced PRP enhancement technology using photothermal biomodulation. Maximise exosome production for superior regenerative results.',
      image: '/mct-hero.png',
      path: '/technology/mct',
      variant: 'default',
    },
    {
      name: 'JBP',
      tagline: 'Nanoneedle & Nanocannula',
      description: 'Premium Japanese precision injection devices. Ultra-fine nanoneedles and nanocannulas for minimally invasive aesthetic procedures.',
      image: 'https://images.unsplash.com/photo-1668453557069-023d287d22f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxtZWRpY2FsJTIwbmVlZGxlJTIwY2FubnVsYSUyMGFlc3RoZXRpYyUyMGluamVjdGlvbiUyMGRldmljZXxlbnwwfHx8fDE3NzUzMDA4OTV8MA&ixlib=rb-4.1.0&q=85',
      path: '/technology/jbp',
      variant: 'default',
    },
    {
      name: 'Promoitalia',
      tagline: 'Advanced Skin Solutions',
      description: 'Premium Italian aesthetic solutions including Rich PL, Skinfill Bacio, and Skinfill Blue for comprehensive skin rejuvenation and enhancement.',
      image: '/promoitalia-logo.png',
      path: '/technology/promoitalia',
      variant: 'default',
    },
    {
      name: 'DQ Paris',
      tagline: 'Aesthetic Innovation from Paris',
      description: 'Cutting-edge French aesthetic technology featuring the Blue Booster for advanced skin revitalisation and hydration.',
      image: '/dqparis-hero.webp',
      path: '/technology/dqparis',
      variant: 'default',
    },
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: 'NZ Exclusive Distributor',
      description: 'Authorized partner bringing premium European aesthetic technologies to New Zealand clinics.',
    },
    {
      icon: Sparkles,
      title: 'Clinically Proven',
      description: 'Medsafe compliant technologies backed by international clinical research.',
    },
    {
      icon: Users,
      title: 'NZ-Based Training',
      description: 'Hands-on training workshops and certification programs throughout New Zealand.',
    },
    {
      icon: Globe,
      title: 'Ongoing Support',
      description: 'Dedicated Auckland-based team providing clinical and business support to NZ practitioners.',
    },
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1581883451416-0c81862fc15b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxnbG93aW5nJTIwc2tpbiUyMG1vZGVsJTIwbHV4dXJ5JTIwYmVhdXR5fGVufDB8fHx8MTc3NTI3NzE5Nnww&ixlib=rb-4.1.0&q=85"
            alt="Luxury aesthetic beauty"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="max-w-2xl"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="overline text-[#595959] mb-6 block"
            >
              New Zealand's Premier Aesthetic Distributor
            </motion.span>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="heading-1 text-[#1A1A1A] mb-8"
              data-testid="hero-headline"
            >
              Advanced Aesthetic Technology for NZ Clinics
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="body-text text-[#595959] text-lg md:text-xl mb-12 max-w-lg"
              data-testid="hero-subheadline"
            >
              Exclusive distributor of world-leading non-surgical treatments. Empowering New Zealand aesthetic practitioners with clinically proven technologies.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <a href="#technologies" className="btn-primary" data-testid="hero-explore-btn">
                Explore Technologies
              </a>
              <a href="#contact" className="btn-secondary" data-testid="hero-contact-btn">
                Contact Us
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
        >
          <span className="overline text-[#595959]">Scroll</span>
          <ArrowDown className="w-5 h-5 text-[#595959] scroll-indicator" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 lg:py-40 bg-[#FDF8F5] relative overflow-hidden" data-testid="about-section">
        {/* Decorative Dermalis Logo Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] md:w-[800px] md:h-[800px] -translate-y-1/4 translate-x-1/4 opacity-[0.04] pointer-events-none">
          <img
            src="/dermalis-icon.png"
            alt=""
            className="w-full h-full object-contain"
            style={{ filter: 'grayscale(100%) brightness(0.3)' }}
          />
        </div>
        
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/5] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1741027369140-7c9ed7220549?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYmVpZ2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzc1Mjc3MjA3fDA&ixlib=rb-4.1.0&q=85"
                alt="Minimalist luxury architecture"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-[#E5E5E5]" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="overline text-[#595959] mb-6 block">About Dermalis</span>
              <h2 className="heading-2 text-[#1A1A1A] mb-8" data-testid="about-headline">
                New Zealand's Trusted Aesthetic Partner
              </h2>
              <div className="space-y-6">
                <p className="body-text text-[#595959] text-lg">
                  Dermalis is New Zealand's leading distributor of advanced aesthetic medical technologies. 
                  Based in Auckland, we partner with cosmetic clinics, dermatologists, and medical practitioners 
                  across New Zealand to deliver world-class non-surgical treatment solutions.
                </p>
                <p className="body-text text-[#595959]">
                  With over two decades of industry expertise, we bring internationally acclaimed technologies 
                  including EndoliftX laser lifting, Jalupro bio-revitalization, Aqualyx fat dissolving, 
                  and Alidya cellulite treatments to the New Zealand aesthetic market.
                </p>
                <p className="body-text text-[#595959]">
                  We provide comprehensive training, clinical support, and ongoing education to ensure 
                  NZ practitioners achieve exceptional patient outcomes. From Auckland to Wellington, 
                  Christchurch to Queenstown — we support aesthetic professionals nationwide.
                </p>
              </div>
              <div className="mt-10">
                <div>
                  <span className="heading-2 text-[#1A1A1A]">22+</span>
                  <p className="overline text-[#595959] mt-2">Years Experience</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-24 md:py-32 lg:py-40" data-testid="technologies-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16 md:mb-24"
          >
            <span className="overline text-[#595959] mb-6 block">Our Technologies</span>
            <h2 className="heading-2 text-[#1A1A1A] max-w-2xl mx-auto" data-testid="technologies-headline">
              Non-Surgical Aesthetic Treatments for NZ Clinics
            </h2>
          </motion.div>

          {/* Technologies Grid - Bento Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technologies.map((tech, index) => (
              <TechnologyCard
                key={tech.name}
                {...tech}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32 lg:py-40 bg-[#1A1A1A]" data-testid="why-choose-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-16 md:mb-24"
          >
            <span className="overline text-white/50 mb-6 block">Why Choose Dermalis NZ</span>
            <h2 className="heading-2 text-white max-w-2xl mx-auto" data-testid="why-choose-headline">
              Your Partner for Aesthetic Excellence in New Zealand
            </h2>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 0.8, 
                  delay: index * 0.1, 
                  ease: [0.22, 1, 0.36, 1] 
                }}
                className="text-center"
                data-testid={`why-choose-item-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-white/20 flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-[#F7E7CE]" />
                </div>
                <h3 className="heading-3 text-white text-xl mb-4">{item.title}</h3>
                <p className="body-text text-white/60">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner CTA Section */}
      <section id="partner" className="py-24 md:py-32 lg:py-40 relative overflow-hidden" data-testid="partner-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1755189118414-14c8dacdb082?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwdGVhbSUyMGhlYWx0aGNhcmUlMjBwcm9mZXNzaW9uYWxzJTIwc21pbGluZ3xlbnwwfHx8fDE3NzUyOTI4MTJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Medical team of practitioners"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/90 to-[#1A1A1A]/70" />
        </div>

        {/* Content */}
        <div className="relative max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="overline text-white/50 mb-6 block"
            >
              For NZ Aesthetic Clinics & Practitioners
            </motion.span>
            
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="heading-2 text-white mb-8"
              data-testid="partner-headline"
            >
              Become a Dermalis Partner in New Zealand
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="body-text text-white/80 text-lg mb-8"
            >
              Join New Zealand's leading network of aesthetic clinics. Access premium non-surgical 
              technologies, comprehensive training programs, and dedicated support to grow your 
              cosmetic practice and deliver exceptional patient results.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-4 mb-10"
            >
              {[
                'Exclusive NZ distribution rights for premium technologies',
                'Hands-on training workshops across New Zealand',
                'Marketing support and patient education materials',
                'Dedicated NZ account manager and clinical support',
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#F7E7CE]" />
                  <span className="body-text text-white/80">{item}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <a 
                href="#contact" 
                className="btn-primary bg-white text-[#1A1A1A] hover:bg-[#F7E7CE] inline-flex items-center gap-3 group"
                data-testid="partner-cta-btn"
              >
                <span>Become a Partner</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 lg:py-40 bg-[#FDF8F5]" data-testid="contact-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="overline text-[#595959] mb-6 block">Contact Dermalis NZ</span>
              <h2 className="heading-2 text-[#1A1A1A] mb-8" data-testid="contact-headline">
                Get in Touch With Our Auckland Team
              </h2>
              <p className="body-text text-[#595959] text-lg mb-12">
                Ready to bring advanced aesthetic technologies to your New Zealand clinic? 
                Contact our Auckland-based team to discuss partnership opportunities, 
                training programs, and how Dermalis can help grow your cosmetic practice.
              </p>

              <div className="space-y-6">
                <div>
                  <span className="overline text-[#595959] block mb-2">Email</span>
                  <a 
                    href="mailto:info@dermalis.co.nz" 
                    className="heading-3 text-[#1A1A1A] hover:text-[#595959] transition-colors"
                    data-testid="contact-email-link"
                  >
                    info@dermalis.co.nz
                  </a>
                </div>
                <div>
                  <span className="overline text-[#595959] block mb-2">Location</span>
                  <p className="heading-3 text-[#1A1A1A]">
                    Auckland, New Zealand
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <div className="bg-white p-8 md:p-12 border border-[#E5E5E5]">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
